import { Component, ViewChild, OnInit, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { FlagGroupService } from '../../flag/flag.group.service';
import { FilterPipe } from './filter.pipe';
import { FlagGroupTag, FlagSubGroup, FlagTemplate, FlagGroup } from '../../shared/interfaces';
import { ToastrService } from 'ngx-toastr';

interface FlatTreeNode {
  id: number;
  name: string;
  label: string;
  level: number;
  expandable: boolean;
  type: 'FlagGroupTag' | 'FlagSubGroup' | 'FlagTemplate';
  node: FlagGroupTag | FlagSubGroup | FlagTemplate;
}

@Component({
  selector: 'app-flag-group-template',
  templateUrl: './flag-group-template.component.html',
  styleUrls: ['./flag-group-template.component.scss'],
  providers: [FilterPipe],
})
export class FlagGroupTemplateComponent implements OnInit {
  @ViewChild('flagGroupTemplateModal', { static: true }) public flagGroupTemplateModal: ModalDirective;
  @ViewChild('confirmationModal', { static: true }) public confirmationModal: ModalDirective;
  @Input() group: FlagGroup;
  @Input() createGroup: boolean = false;
  @Input() inspectionId: number;
  @Input() expand: boolean = true;
  @Input() CreateGroup: boolean = true;
  @Input() allGroups: [];
  @Input() flagGroupTags: FlagGroupTag[] = [];
  @Output() messageEvent = new EventEmitter<any>();
  @Output() editGroup = new EventEmitter<any>();
  changeSet: any = {};

  loading: boolean = true;
  open: boolean = false;
  searchQuery: string = '';
  selectedTemplateId: number | null = null;
  flagTemplates: FlagTemplate[] = [];
  preview: boolean = false;
  showTemplate: FlagTemplate = null;
  highlight: boolean = false;
  treeData: FlagGroupTag[] = [];
  highlightNodes: any[] = [];

  private transformer = (node: FlagGroupTag | FlagSubGroup | FlagTemplate, level: number): FlatTreeNode => ({
    id: node.id,
    name: node.name || '',
    label: node.label,
    level: level,
    expandable: !!((node['subgroups'] && node['subgroups'].length) || (node['templates'] && node['templates'].length)),
    type: node['subgroups'] ? 'FlagGroupTag' : node['templates'] ? 'FlagSubGroup' : 'FlagTemplate',
    node: node,
  });

  treeControl = new FlatTreeControl<FlatTreeNode>(
    node => node.level,
    node => node.expandable,
  );

  treeFlattener = new MatTreeFlattener<FlagGroupTag | FlagSubGroup | FlagTemplate, FlatTreeNode>(
    this.transformer,
    node => node.level,
    node => node.expandable,
    node => node['subgroups'] || node['templates'] || [],
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  constructor(
    private flagGroupService: FlagGroupService,
    private filterPipe: FilterPipe,
    private toast: ToastrService,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      this.flagGroupTemplateModal.onHidden.subscribe(x => {
        if (x.dismissReason === 'esc' || x.dismissReason === 'backdrop-click') {
          this.cancel();
        }
      });
      this.dataSource.data = this.flagGroupTags;
      if (this.expand) {
        await this.expandDefaultNodes();
      }

      this.loading = false;
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['flagGroupTags']?.currentValue) {
      this.dataSource.data = this.flagGroupTags;
    }
  }

  expandDefaultNodes() {
    const nodesToExpand = this.findMatchingNodes(this.treeControl.dataNodes, this.group);
    this.changeSet = {
      oldGroup: nodesToExpand[0]?.name || '',
      oldSubgroup: nodesToExpand[1]?.name || '',
    };
    this.highlightNodes = nodesToExpand;
    nodesToExpand.forEach(node => this.expandAllDescendants(node));
  }

  findMatchingNodes(nodes: any[], group: any): any[] {
    return nodes.filter(node => {
      const nodeTemplatesMatch = node.node?.templates?.some(template => this.isTemplateMatching(template, group));
      const subGroupTemplatesMatch = node.node?.subgroups?.some(subgroup =>
        subgroup.templates?.some(template => this.isTemplateMatching(template, group)),
      );

      return nodeTemplatesMatch || subGroupTemplatesMatch;
    });
  }

  isTemplateMatching(template: any, group: any): boolean {
    const normalize = (value: any) => (value ? value.toString().trim().replace(/\s+/g, ' ') : '');
    const fields = [
      'description',
      'violation.elevatedRisk',
      'violation.whyDoesThisMatter',
      'violation.actionRequested',
      'violation.documentationRequested',
      'violation.url',
      'violation.urlTitle',
    ];

    return fields.every(field => {
      const templateValue = normalize(field.split('.').reduce((o, i) => o?.[i], template));
      const groupValue = normalize(field.split('.').reduce((o, i) => o?.[i], group));

      return templateValue === groupValue;
    });
  }

  expandAllDescendants(node: FlatTreeNode) {
    this.treeControl.expand(node);

    const childNode = node.node as FlagTemplate;
    if (
      node.type === 'FlagTemplate' &&
      childNode.description === this.group.description &&
      childNode.subtype.typeId === this.group.subtype.typeId &&
      childNode.subtype.id === this.group.subtype.id &&
      childNode.violation.elevatedRisk === this.group.violation.elevatedRisk &&
      childNode.violation.whyDoesThisMatter === this.group.violation.whyDoesThisMatter &&
      childNode.violation.actionRequested === this.group.violation.actionRequested &&
      childNode.violation.documentationRequested === this.group.violation.documentationRequested &&
      childNode.violation.url === this.group.violation.url &&
      childNode.violation.urlTitle === this.group.violation.urlTitle
    ) {
      this.previewTemplate(childNode);
      this.selectedTemplateId = node.node.id;
      this.highlight = true;
      this.changeSet.oldTemplate = node.node?.name || '';
    }

    const childNodes = this.treeControl.dataNodes.filter(
      child => child.level === node.level + 1 && this.isChildNode(node, child),
    );

    childNodes.forEach(child => this.expandAllDescendants(child));
  }

  isChildNode(parent: FlatTreeNode, child: FlatTreeNode): boolean {
    if (parent.type === 'FlagGroupTag' && parent.node['subgroups']) {
      return (parent.node['subgroups'] as FlagSubGroup[]).some(subgroup => subgroup.id === child.id);
    } else if (parent.type === 'FlagSubGroup' && parent.node['templates']) {
      return (parent.node['templates'] as FlagTemplate[]).some(template => template.id === child.id);
    }
    return false;
  }

  hasChild = (_: number, node: FlatTreeNode) => node.expandable;

  apply() {
    if (
      this.group.subtype.typeId != this.showTemplate.subtype.typeId ||
      this.group.subtype.id != this.showTemplate.subtype.id
    ) {
      this.confirmationModal.show();
    } else {
      if (
        this.group.description != null ||
        this.group.violation.elevatedRisk != null ||
        this.group.violation.whyDoesThisMatter != null ||
        this.group.violation.actionRequested != null ||
        this.group.violation.documentationRequested != null ||
        this.group.violation.url != null ||
        this.group.violation.urlTitle != null ||
        this.group.helpPictures.length > 0 ||
        this.group.violation.internalNotes != null
      ) {
        if (
          this.group.description != this.showTemplate.description ||
          this.group.violation.elevatedRisk != this.showTemplate.violation.elevatedRisk ||
          this.group.violation.whyDoesThisMatter != this.showTemplate.violation.whyDoesThisMatter ||
          this.group.violation.actionRequested != this.showTemplate.violation.actionRequested ||
          this.group.violation.documentationRequested != this.showTemplate.violation.documentationRequested ||
          this.group.violation.url != this.showTemplate.violation.url ||
          this.group.violation.urlTitle != this.showTemplate.violation.urlTitle ||
          JSON.stringify(this.group.helpPictures) !== JSON.stringify(this.showTemplate.helpPictures) ||
          this.group.violation.internalNotes != this.showTemplate.violation.internalNotes
        ) {
          this.confirmationModal.show();
        } else {
          this.open = false;
          this.reset();
          this.flagGroupTemplateModal.hide();
        }
      } else {
        this.applyConfirmation();
      }
    }
  }

  async show() {
    this.dataSource.data = this.flagGroupTags;
    this.flagGroupTemplateModal.show();
    this.open = true;
    if (this.expand) {
      await this.expandDefaultNodes();
    }
  }

  cancel() {
    this.flagGroupTemplateModal.hide();
    this.reset();
  }

  async previewTemplate(node: FlagTemplate) {
    this.selectedTemplateId = node.id;
    this.preview = true;
    this.showTemplate = node;
  }

  clearSearch() {
    this.reset();
  }

  reset() {
    this.preview = false;
    this.selectedTemplateId = null;
    this.searchQuery = '';
    this.dataSource.data = this.flagGroupTags;
  }

  onNodeToggle() {
    this.preview = false;
    this.selectedTemplateId = null;
    this.highlight = false;
  }

  applyFilter() {
    this.preview = false;
    this.selectedTemplateId = null;

    if (!this.searchQuery) {
      this.dataSource.data = this.flagGroupTags;
    } else {
      const filteredData = this.filterPipe.transform(this.flagGroupTags, this.searchQuery);
      this.dataSource.data = filteredData;
      this.treeControl.expandAll();
    }
  }

  violationData = {
    violation_level_1: 'Immediate action required',
    violation_level_2: 'Recommended to reduce the risk of loss',
    violation_level_3: 'Recommendation',
  };

  getViolationValue(key: string) {
    if (this.violationData.hasOwnProperty(key)) {
      return this.violationData[key];
    } else {
      return `Template has no Flag Sub Type`;
    }
  }

  cancelConfirmation() {
    this.confirmationModal.hide();
  }

  async applyConfirmation() {
    await this.changeGroupData();
    this.confirmationModal.hide();
    this.open = false;
    this.messageEvent.emit(true);
    this.editGroup.emit(this.group);
    this.toast.success('Template Applied');
  }

  async changeGroupData() {
    this.group.description = this.showTemplate.description;
    this.group.subtype = this.showTemplate.subtype;
    this.group.name = this.showTemplate.name;

    this.group.violation = this.showTemplate.violation;
    this.group.discrepancy = this.showTemplate.discrepancy;
    this.group.helpPictures = this.showTemplate.helpPictures;
    this.findGroupAndSubGroup(this.showTemplate.subgroupId);
    this.group.changeSet = this.changeSet;
  }

  async createGroupWithTemplate() {
    this.loading = true;
    let group;
    if (this.group) {
      this.group.description = this.showTemplate.description;
      this.group.subtype = this.showTemplate.subtype;

      this.group.name = this.showTemplate.name;

      this.group.violation = this.showTemplate.violation;
      this.group.discrepancy = this.showTemplate.discrepancy;
      this.group.helpPictures = this.showTemplate.helpPictures;

      group = await this.flagGroupService.create(this.inspectionId, this.group, true);
      group['new'] = true;
      this.confirmationModal.hide();
      this.open = false;
      this.loading = false;
      this.messageEvent.emit(group);
    } else {
      this.findGroupAndSubGroup(this.showTemplate.subgroupId);
      this.messageEvent.emit({
        ...this.showTemplate,
        ...{ groupName: this.showTemplate.name, changeSet: this.changeSet },
      });
    }
  }

  findGroupAndSubGroup(subgroupId: number) {
    for (const group of this.flagGroupTags) {
      const matchedSubgroup = group.subgroups.find(subgroup => subgroup.id === subgroupId);

      if (matchedSubgroup) {
        this.changeSet.newTemplate = this.showTemplate.name;
        this.changeSet.newSubgroup = matchedSubgroup.name;
        this.changeSet.newGroup = group.name;
      }
    }
  }
}
